import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useRouteMatch} from 'react-router-dom'
import NoneProfitSummary from 'components/molcules/N1SummaryReport/EEG/EEGSummary'
import NoneProfitSummaryAPI from 'components/molcules/N1SummaryReport/EEG/EEGSummaryAPI'
import NoneChildProfitSummary from 'components/molcules/N1SummaryReport/EEGChild/EEGSummary'
import ProfitSummary from 'components/molcules/ProfitSummaryReport/EEG/EEGSummary'
import ProfitSummaryAPI from 'components/molcules/ProfitSummaryReport/EEG/EEGSummaryAPI'
import HeaderV1 from 'pages/HealthCenter/Report/Elements/HeaderV1'
import useLanguageReport from 'hooks/useLanguageReport'
import NoneChildProfitSummaryAPI from 'components/molcules/N1SummaryReport/EEGChild/EEGSummaryAPI'
import useEegSummaryReport from 'features/analysis/useEegSummaryReport'
import useLanguage from 'features/languageReport/useLanguage'
import {
  EEGAnalysisSummaryReportResponse,
  EEGSummaryResponseWithClient,
  fetchAnalysisSummaryReportApi,
} from 'api/analysisApi'
import {GenderValue, toGenderString} from 'constants/CommonConstant'
import useStyles from 'pages/Analysis/EEGSummary/Report/style'
import {INIT_EEG_SUMMARY_ANALYSIS_REPORT} from 'pages/Analysis/EEGSummary/constants'
import {ResHealthReport} from 'api/languageApi'

interface BrainFunctionMappingProps {
  setIsChild: (isChild: boolean) => void
  textHealthAPI: ResHealthReport | null
}
const BrainFunctionMapping = ({
  setIsChild,
  textHealthAPI,
}: BrainFunctionMappingProps) => {
  const {t, i18n} = useTranslation()
  const {textEegAPI} = useLanguage('eeg')

  const {
    params: {id},
  } = useRouteMatch<{id: string}>()
  const {isLocalLanguage} = useLanguageReport('eeg')
  const [report, setReport] = useState<EEGSummaryResponseWithClient>()
  const [analysis, setAnalysis] = useState<EEGAnalysisSummaryReportResponse>(
    INIT_EEG_SUMMARY_ANALYSIS_REPORT,
  )
  const [titleHeader, setTitleHeader] = useState<string>('')

  const {itemsBrainFunctionMapping: items, getReportBrainFunctionMapping} =
    useEegSummaryReport()

  const getAnalysisReport = async () => {
    const response = await fetchAnalysisSummaryReportApi(
      Number.parseInt(id, 10),
      i18n.language as LanguageCode,
    )
    setAnalysis(response)
  }

  const classes = useStyles()
  const isSummary = true

  useEffect(() => {
    if (items) {
      setReport(items[0])
      const age = items[0]?.client?.treatmentAge ?? 0
      if (age >= 20) {
        setIsChild(true)
        setTitleHeader(String(t('IBrainFunctionMapping')))
      } else {
        setIsChild(false)
        setTitleHeader(String(t('IEEGChildSummaryReport')))
      }
    }
  }, [items, textEegAPI])

  useEffect(() => {
    const param = {
      lang: i18n.language,
      summaryIds: [Number.parseInt(id, 10)],
      summaryLang: i18n.language as LanguageCode,
    }
    getReportBrainFunctionMapping(param)
    getAnalysisReport()
  }, [i18n.language])

  return (
    <div className={classes.reportContainer}>
      <HeaderV1
        title={
          isLocalLanguage
            ? titleHeader
            : textHealthAPI?.brain_connectivity.title ?? ''
        }
        content={
          isLocalLanguage
            ? t('IEEGSummarySubTitle')
            : textHealthAPI?.header.subject_info_guide ?? ''
        }
        subTitle={textEegAPI?.title.sub ?? ''}
        name={t('IGetFullName', {
          firstName: report?.client.clientFirstName ?? '',
          lastName: report?.client.clientLastName ?? '',
        })}
        sex={
          report
            ? t(toGenderString(report.client.clientGender as GenderValue))
            : ''
        }
        birthday={report ? report.client.clientBirth.replaceAll('-', '.') : ''}
        measurementDate={
          report
            ? report.displayMeasurementDateTime ??
              (report.treatmentAt as string)
            : ''
        }
        textHeader={textEegAPI?.header}
        isLocalLanguage={isLocalLanguage}
        eceo={report?.client?.eceo ?? ''}
      />

      <div className={classes.brainBasic}>
        {report !== undefined &&
          report.reportType === 'N1' &&
          report.client.treatmentAge >= 20 &&
          (isLocalLanguage ? (
            <NoneProfitSummary
              {...report}
              contourImage={analysis.images.brain_aging_contour}
              threeDimensionImage={analysis.images.brain_aging_3D}
              total={analysis.scores.brain_mapping?.total_score || 0}
              isSummary={isSummary}
            />
          ) : (
            <NoneProfitSummaryAPI
              {...report}
              contourImage={analysis.images.brain_aging_contour}
              threeDimensionImage={analysis.images.brain_aging_3D}
              total={analysis.scores.brain_mapping?.total_score || 0}
              isSummary={isSummary}
            />
          ))}
        {report !== undefined &&
          report.reportType !== 'N1' &&
          report.client.treatmentAge >= 20 &&
          (isLocalLanguage ? (
            <ProfitSummary
              {...report}
              contourImage={analysis.images.brain_aging_contour}
              threeDimensionImage={analysis.images.brain_aging_3D}
              total={analysis.scores.brain_mapping?.total_score || 0}
              lobe={analysis.scores.brain_mapping?.lobe_scores}
              roi={analysis.scores.brain_mapping?.roi_scores}
              isSummary={isSummary}
            />
          ) : (
            <ProfitSummaryAPI
              {...report}
              contourImage={analysis.images.brain_aging_contour}
              threeDimensionImage={analysis.images.brain_aging_3D}
              total={analysis.scores.brain_mapping?.total_score || 0}
              lobe={analysis.scores.brain_mapping?.lobe_scores}
              roi={analysis.scores.brain_mapping?.roi_scores}
              isSummary={isSummary}
            />
          ))}
        {report !== undefined &&
          report.reportType === 'N1' &&
          report.client.treatmentAge < 20 &&
          (isLocalLanguage ? (
            <NoneChildProfitSummary
              {...report}
              frontalLobeDevelopment={
                analysis.scores.infant_youth.frontal_development
              }
              frontalLobeDevelopmentGraph_image={
                analysis.images.frontal_development_graph
              }
              informationProcessingSpeedGraphImage={
                analysis.images.processing_speed_graph
              }
              informationProcessingSpeed={
                analysis.scores.infant_youth.information_processing_speed
              }
              alphaBandDevelopment={
                analysis.scores.infant_youth.alpha_development
              }
              alphaBandDevelopmentBarGraphImage={
                analysis.images.alpha_development_bar_graph
              }
              alphaBandDevelopmentLineGraphImage={
                analysis.images.alpha_development_line_graph
              }
              brainBalance={analysis.scores.infant_youth.brain_balance}
              brainBalance3dImage={analysis.images.brain_balance_3D}
              brainBalanceImage={analysis.images.brain_balance}
              brainAgeGraphImage={analysis.images.brain_age_graph}
              brainAge={analysis.scores.infant_youth.brain_age}
              contourImage={analysis.images.brain_aging_contour}
              threeDimensionImage={analysis.images.brain_aging_3D}
              total={analysis.scores.brain_mapping?.total_score || 0}
              lobe={analysis.scores.brain_mapping?.lobe_scores}
              isSummary={isSummary}
            />
          ) : (
            <NoneChildProfitSummaryAPI
              {...report}
              frontalLobeDevelopment={
                analysis.scores.infant_youth.frontal_development
              }
              frontalLobeDevelopmentGraph_image={
                analysis.images.frontal_development_graph
              }
              informationProcessingSpeedGraphImage={
                analysis.images.processing_speed_graph
              }
              informationProcessingSpeed={
                analysis.scores.infant_youth.information_processing_speed
              }
              alphaBandDevelopment={
                analysis.scores.infant_youth.alpha_development
              }
              alphaBandDevelopmentBarGraphImage={
                analysis.images.alpha_development_bar_graph
              }
              alphaBandDevelopmentLineGraphImage={
                analysis.images.alpha_development_line_graph
              }
              brainBalance={analysis.scores.infant_youth.brain_balance}
              brainBalance3dImage={analysis.images.brain_balance_3D}
              brainBalanceImage={analysis.images.brain_balance}
              brainAgeGraphImage={analysis.images.brain_age_graph}
              brainAge={analysis.scores.infant_youth.brain_age}
              contourImage={analysis.images.brain_aging_contour}
              threeDimensionImage={analysis.images.brain_aging_3D}
              total={analysis.scores.brain_mapping?.total_score || 0}
              lobe={analysis.scores.brain_mapping?.lobe_scores}
              isSummary={isSummary}
            />
          ))}
        {report !== undefined &&
          report.reportType !== 'N1' &&
          report.client.treatmentAge < 20 &&
          (isLocalLanguage ? (
            <NoneChildProfitSummary
              {...report}
              frontalLobeDevelopment={
                analysis.scores.infant_youth.frontal_development
              }
              frontalLobeDevelopmentGraph_image={
                analysis.images.frontal_development_graph
              }
              informationProcessingSpeedGraphImage={
                analysis.images.processing_speed_graph
              }
              informationProcessingSpeed={
                analysis.scores.infant_youth.information_processing_speed
              }
              alphaBandDevelopment={
                analysis.scores.infant_youth.alpha_development
              }
              alphaBandDevelopmentBarGraphImage={
                analysis.images.alpha_development_bar_graph
              }
              alphaBandDevelopmentLineGraphImage={
                analysis.images.alpha_development_line_graph
              }
              brainBalance={analysis.scores.infant_youth.brain_balance}
              brainBalance3dImage={analysis.images.brain_balance_3D}
              brainBalanceImage={analysis.images.brain_balance}
              brainAgeGraphImage={analysis.images.brain_age_graph}
              brainAge={analysis.scores.infant_youth.brain_age}
              contourImage={analysis.images.brain_aging_contour}
              threeDimensionImage={analysis.images.brain_aging_3D}
              total={analysis.scores.brain_mapping?.total_score || 0}
              lobe={analysis.scores.brain_mapping?.lobe_scores}
              roi={analysis.scores.brain_mapping?.roi_scores}
              isSummary={isSummary}
            />
          ) : (
            <NoneChildProfitSummaryAPI
              {...report}
              frontalLobeDevelopment={
                analysis.scores.infant_youth.frontal_development
              }
              frontalLobeDevelopmentGraph_image={
                analysis.images.frontal_development_graph
              }
              informationProcessingSpeedGraphImage={
                analysis.images.processing_speed_graph
              }
              informationProcessingSpeed={
                analysis.scores.infant_youth.information_processing_speed
              }
              alphaBandDevelopment={
                analysis.scores.infant_youth.alpha_development
              }
              alphaBandDevelopmentBarGraphImage={
                analysis.images.alpha_development_bar_graph
              }
              alphaBandDevelopmentLineGraphImage={
                analysis.images.alpha_development_line_graph
              }
              brainBalance={analysis.scores.infant_youth.brain_balance}
              brainBalance3dImage={analysis.images.brain_balance_3D}
              brainBalanceImage={analysis.images.brain_balance}
              brainAgeGraphImage={analysis.images.brain_age_graph}
              brainAge={analysis.scores.infant_youth.brain_age}
              contourImage={analysis.images.brain_aging_contour}
              threeDimensionImage={analysis.images.brain_aging_3D}
              total={analysis.scores.brain_mapping?.total_score || 0}
              lobe={analysis.scores.brain_mapping?.lobe_scores}
              roi={analysis.scores.brain_mapping?.roi_scores}
              isSummary={isSummary}
            />
          ))}
      </div>
    </div>
  )
}

export default BrainFunctionMapping
